import Mutation from './mutationTypes';
import { apiLoadTOC, apiGetSecitonDiffSummary, apiLoadPromptOptions, apiUpdateSummaryRating, apiCheckDiffSummaryStatus, apiGetDiffSummaryByJobId, apiGetCompareHistory, apiDeleteCompareHistory, apiGetCompareQueryCount  } from "@/components/AICompare/api"

const AI_COMPARE_STATUS_INITIAL_DELAY_MS = 10000;
const AI_COMPARE_STATUS_POLL_INTERVAL_MS = 5000;
const AI_COMPARE_STATUS_RETRY_MAX_DELAY_MS = 30000;
const AI_COMPARE_STATUS_MAX_WAIT_MS = 30 * 60 * 1000;

const beginComparePollingRun = (commit, getters) => {
    const currentRunId = parseInt(getters.getItem('comparePollingRunId'), 10) || 0;
    const nextRunId = currentRunId + 1;
    commit(Mutation.SET_ITEM, {field: 'comparePollingRunId', value: nextRunId});
    return nextRunId;
};

const clearCompareLoadingState = (commit) => {
    commit(Mutation.SET_ITEM, {field: 'loading', value: false});
    commit(Mutation.SET_ITEM, {field: 'compareSummaryLoading', value: false});
};

const isTransientPollingError = (error) => {
    if(!error || error.isAxiosError !== true) {
        return false;
    }
    if(!error.response) {
        return true;
    }
    const status = parseInt(error.response.status, 10);
    return status === 408 || status === 425 || status === 429 || status >= 500;
};

export default {
    setItem: ({ commit }, {field, value}) => {
        commit(Mutation.SET_ITEM, {field, value});
    },
    initComparePDF: async ({ commit, dispatch  }, compareDetails) => {
        try {
            commit(Mutation.SET_ITEM, {field: 'showCompareWindow', value: true});
            commit(Mutation.SET_ITEM, {field: 'toc1Selection', value: {}});
            commit(Mutation.SET_ITEM, {field: 'toc2Selection', value: {}});
            commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: ''});
            commit(Mutation.SET_ITEM, {field: 'diffSummaryId', value: 0});
            commit(Mutation.SET_ITEM, {field: 'selectedRating', value: -1});
            clearCompareLoadingState(commit);
            commit(Mutation.SET_ITEM, {field: 'initLoading', value: false});
            commit(Mutation.SET_ITEM, {field: 'prevActiveTabs', value: []});
            commit(Mutation.SET_ITEM, {field: 'promptOptionsLoading', value: false});
            commit(Mutation.SET_ITEM, {field: 'tocByItemId', value: {}});
            commit(Mutation.SET_ITEM, {field: 'tocLoadErrors', value: {}});
            commit(Mutation.SET_ITEM, {field: 'tocResponseState', value: 'ready'});
            commit(Mutation.SET_ITEM, {field: 'tocModalNoticeKey', value: ''});
            commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: []});
            commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: []});
            for (const key in compareDetails) {
                commit(Mutation.SET_ITEM, {field: key, value: compareDetails[key]});
            }
            commit(Mutation.SET_ITEM, {field: 'availableItems', value: compareDetails.availableItems});
            commit(Mutation.SET_ITEM, {field: 'loading', value: true});
            const tocResponse = await dispatch('loadTOCsForCompare', {
                itemIds: [compareDetails.itemId1, compareDetails.itemId2]
            });
            const tocByItemId = tocResponse.tocByItemId;
            commit(Mutation.SET_ITEM, {field: 'toc1', value: tocByItemId[compareDetails.itemId1] || []});
            commit(Mutation.SET_ITEM, {field: 'toc2', value: tocByItemId[compareDetails.itemId2] || []});
            dispatch('setActivePage', {activeTab: 'compare-versions', tabPage: 'compare-toc'});
            if (tocResponse.state === 'processing') {
                commit(Mutation.SET_ITEM, {field: 'loading', value: false});
                commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: tocResponse.processingItems});
                commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: []});
                return;
            }
            if (tocResponse.state === 'failed') {
                commit(Mutation.SET_ITEM, {field: 'loading', value: false});
                commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: []});
                commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: tocResponse.failedItems});
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: ''});
                return;
            }
            if (tocResponse.state !== 'ready') {
                commit(Mutation.SET_ITEM, {field: 'loading', value: false});
                commit(Mutation.SET_ITEM, {field: 'tocModalNoticeKey', value: 'ai_compare_toc_unavailable'});
                return;
            }
            commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: []});
            commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: []});
            if (!tocByItemId[compareDetails.itemId1] || !tocByItemId[compareDetails.itemId2]) {
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: 'ai_compare_toc_unavailable'});
            }
            commit(Mutation.SET_ITEM, {field: 'loading', value: false});
        } catch(error) {
            commit(Mutation.SET_ITEM, {field: 'loading', value: false});
            commit(Mutation.SET_ITEM, {field: 'tocModalNoticeKey', value: 'ai_compare_toc_unavailable'});
            console.log('API call failed while loading PDF table of contents');
            console.log(error);
        }
    },
    loadTOCsForCompare: async ({ commit, getters }, { itemIds }) => {
        const uniqueItemIds = [];
        itemIds.forEach((itemId) => {
            const normalizedItemId = parseInt(itemId, 10);
            if (normalizedItemId > 0 && uniqueItemIds.indexOf(normalizedItemId) === -1) {
                uniqueItemIds.push(normalizedItemId);
            }
        });
        let tocByItemId = Object.assign({}, getters.getItem('tocByItemId') || {});
        let tocLoadErrors = Object.assign({}, getters.getItem('tocLoadErrors') || {});
        let state = 'ready';
        let processingItems = [];
        let failedItems = [];
        const itemIdsToLoad = uniqueItemIds.filter((itemId) => !tocByItemId[itemId]);

        if (itemIdsToLoad.length) {
            const loadTOCApiAbortController = new AbortController();
            commit(Mutation.SET_ITEM, {field: 'abortController.loadTOCApi', value: loadTOCApiAbortController});
            const response = await apiLoadTOC({
                itemIds: uniqueItemIds,
                docId: getters.getItem('docId'),
                docName: getters.getItem('docName')
            }, loadTOCApiAbortController);
            state = response.state || 'ready';
            commit(Mutation.SET_ITEM, {field: 'tocResponseState', value: state});

            const items = Array.isArray(response.items) ? response.items : [];
            items.forEach((item) => {
                if (item && item.uid && Array.isArray(item.toc) && item.toc.length) {
                    tocByItemId[item.uid] = item.toc;
                    delete tocLoadErrors[item.uid];
                }
            });

            const errors = Array.isArray(response.errors) ? response.errors : [];
            errors.forEach((error) => {
                if (error && error.uid) {
                    tocLoadErrors[error.uid] = error.reason || 'TOC_UNAVAILABLE';
                }
            });
            processingItems = Array.isArray(response.processing) ? response.processing : [];
            failedItems = Array.isArray(response.failed) ? response.failed : [];
            commit(Mutation.SET_ITEM, {field: 'tocByItemId', value: tocByItemId});
            commit(Mutation.SET_ITEM, {field: 'tocLoadErrors', value: tocLoadErrors});
        }

        return { state, tocByItemId, tocLoadErrors, processingItems, failedItems };
    },
    loadComparePromptOptions: async ({ commit, state }) => {
        try {
            let promptOptions = state.promptOptions;
            if(promptOptions.length == 0) {
                commit(Mutation.SET_ITEM, {field: 'promptOptionsLoading', value: true});
                const promptOptions =  await apiLoadPromptOptions();
                if(promptOptions.prompts) {
                    promptOptions.prompts.unshift({id: 0, title: 'Select response format...'});
                    commit(Mutation.SET_ITEM, {field: 'promptOptions', value: promptOptions.prompts});
                }
                commit(Mutation.SET_ITEM, {field: 'promptOptionsLoading', value: false});
            }
        } catch(error) {
            commit(Mutation.SET_ITEM, {field: 'promptOptionsLoading', value: false});
            console.log('failed to load prompt options');
            console.log(error);
        }
    },
    getSecitonDiffSummary: async ({ commit, getters, dispatch }, compareParams) => {
        const pollingRunId = beginComparePollingRun(commit, getters);
        const isPollingRunActive = () => {
            return getters.getItem('comparePollingRunId') === pollingRunId
                && !getters.getItem('processAborted');
        };
        const updateStateForError = () => {
            if(!isPollingRunActive()) {
                return;
            }
            clearCompareLoadingState(commit);
            commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: 'failed_to_compare'});
        };
        try {
            commit(Mutation.SET_ITEM, {field: 'loading', value: true});
            commit(Mutation.SET_ITEM, {field: 'compareSummaryLoading', value: true});
            commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: ''});
            let compareApiAbortController =  new AbortController();
            commit(Mutation.SET_ITEM, {field: 'abortController.compareApi', value: compareApiAbortController});
            const { jobId, limitError} =  await apiGetSecitonDiffSummary(compareParams, compareApiAbortController);
            if(!isPollingRunActive()) {
                return;
            }
            if(jobId) {
                const pollingDeadline = Date.now() + AI_COMPARE_STATUS_MAX_WAIT_MS;
                let consecutivePollingFailures = 0;
                let checkDiffSummaryStatusCallback = async function() {
                    if(!isPollingRunActive()) {
                        return;
                    }
                    if(Date.now() >= pollingDeadline) {
                        updateStateForError();
                        return ;
                    }
                    try {
                        let checkDiffStatusApiAbortController =  new AbortController();
                        commit(Mutation.SET_ITEM, {field: 'abortController.checkDiffStatusApi', value: checkDiffStatusApiAbortController});
                        const response = await apiCheckDiffSummaryStatus(jobId, checkDiffStatusApiAbortController);
                        if(!isPollingRunActive()) {
                            return;
                        }
                        if(!response.status) {
                            updateStateForError();
                            return;
                        }
                        switch(response.status) {
                            case 'PENDING':
                            case 'PROCESSING':
                                consecutivePollingFailures = 0;
                                scheduleDiffSummaryStatusCheck(AI_COMPARE_STATUS_POLL_INTERVAL_MS);
                                break;
                            case 'FINISHED': {
                                const response = await apiGetDiffSummaryByJobId(jobId);
                                if(!isPollingRunActive()) {
                                    return;
                                }
                                if(response && response.aiMessage && response.id) {
                                    consecutivePollingFailures = 0;
                                    //when finished, call the api to save the response
                                    commit(Mutation.SET_ITEM, {field: 'diffSummary', value: response.aiMessage});
                                    commit(Mutation.SET_ITEM, {field: 'diffSummaryId', value: response.id});
                                    dispatch('setActivePage', {activeTab: 'compare-versions', tabPage: 'compare-summary'});
                                    clearCompareLoadingState(commit);
                                    commit(Mutation.SET_ITEM, {field: 'newCompare', value: true});
                                    dispatch('updateQuotaUsed'); //update quota used
                                } else {
                                    updateStateForError();
                                }
                                break;
                            }
                            case 'ERROR':
                            default:
                                updateStateForError();
                            break;
                        }
                    } catch(error) {
                        if(!isPollingRunActive()) {
                            return;
                        }
                        if(!isTransientPollingError(error)) {
                            updateStateForError();
                            console.log(error);
                            return;
                        }
                        consecutivePollingFailures += 1;
                        const retryDelay = Math.min(
                            AI_COMPARE_STATUS_POLL_INTERVAL_MS * Math.pow(2, consecutivePollingFailures - 1),
                            AI_COMPARE_STATUS_RETRY_MAX_DELAY_MS
                        );
                        scheduleDiffSummaryStatusCheck(retryDelay);
                        console.log(error);
                    }
                }
                const scheduleDiffSummaryStatusCheck = (delay) => {
                    if(!isPollingRunActive()) {
                        return;
                    }
                    const remainingWait = pollingDeadline - Date.now();
                    if(remainingWait <= 0) {
                        updateStateForError();
                        return;
                    }
                    setTimeout(checkDiffSummaryStatusCallback, Math.min(delay, remainingWait));
                };
                scheduleDiffSummaryStatusCheck(AI_COMPARE_STATUS_INITIAL_DELAY_MS);
            } else if(limitError === 'subscription_compare_limit_exceeded')  {
                clearCompareLoadingState(commit);
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: 'query_limit_exceeded'});
            } else if(limitError === 'user_compare_limit_exceeded')  {
                clearCompareLoadingState(commit);
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: 'user_compare_limit_exceeded'});
            }
             else {
                updateStateForError();
            }
        } catch(error) {
            if(!isPollingRunActive()) {
                return;
            }
            updateStateForError();
            console.log('api call failed for getting reply');
            console.log(error);
        }
    },
    updateSummaryRating: async ({ commit },{ summaryId, rating }) => {
        try {
            commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: ''});
            commit(Mutation.SET_ITEM, {field: 'updateRatingInProgress', value: true});
            await apiUpdateSummaryRating({ summaryId, rating });
            commit(Mutation.SET_ITEM, {field: 'updateRatingInProgress', value: false});
        } catch(error) {
            commit(Mutation.SET_ITEM, {field: 'updateRatingInProgress', value: false});
            commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: 'failed_to_rate'});
            console.log('api call failed for getting reply');
            console.log(error);
        }
    },
    closeCompare:  ({ commit, getters }) => {
        beginComparePollingRun(commit, getters);
        clearCompareLoadingState(commit);
        commit(Mutation.SET_ITEM, {field: 'showCompareWindow', value: false});
    },
    abortApiCall: ({ commit, getters }) => {
        beginComparePollingRun(commit, getters);
        clearCompareLoadingState(commit);
        let checkDiffStatusApi = getters.getItem('abortController.checkDiffStatusApi');
        if(checkDiffStatusApi) {
            checkDiffStatusApi.abort();
        }
        let compareApi = getters.getItem('abortController.compareApi');
        if(compareApi) {
            compareApi.abort();
        }
        let loadTOCApi = getters.getItem('abortController.loadTOCApi');
        if(loadTOCApi) {
            loadTOCApi.abort();
        }
    },
    setActivePage({commit, getters},{activeTab, tabPage}) {
        if( getters.getItem('activeTab') !='' && (activeTab != getters.getItem('activeTab') || tabPage != getters.getItem('tabPage'))) {
            let prevActiveTabs = getters.getItem('prevActiveTabs');
            prevActiveTabs.unshift({activeTab: getters.getItem('activeTab'), tabPage: getters.getItem('tabPage')});
            commit(Mutation.SET_ITEM, {field: 'prevActiveTabs', value: prevActiveTabs});
        }
        commit(Mutation.SET_ITEM, {field: 'activeTab', value: activeTab});
        commit(Mutation.SET_ITEM, {field: 'tabPage', value: tabPage});

    },
    setActivePageForCompareHistory: ({ commit, getters,dispatch },{activeTab, tabPage}) => {
        dispatch('setActivePage', {activeTab, tabPage});
        if(getters.getItem('newCompare')) {
            dispatch('resetCompareHistoryFilters');
            dispatch('loadCompareHistory');
            commit(Mutation.SET_ITEM, {field: 'newCompare', value: false});
        }
    },
    loadCompareHistory: async ({ commit, state }) => {
        try {
            commit(Mutation.SET_ITEM, {field: 'compareHistoryLoading', value: true});
            const params = {
                docId: state.docId,
                offset: (state.compareHistoryPage - 1) * state.compareHistoryItemsPerPage,
                limit: state.compareHistoryItemsPerPage,
                sortBy: state.compareHistorySortBy || 'date',
                sortOrder: state.compareHistorySortDesc ? 'asc' : 'desc',
                startDate: state.compareHistoryDateRange[0],
                endDate: state.compareHistoryDateRange[1],
                priorVersion: state.compareHistoryPriorVersion ? state.compareHistoryPriorVersion : '',
                newerVersion: state.compareHistoryNewerVersion ? state.compareHistoryNewerVersion : '',
            };
            const result = await apiGetCompareHistory(params.docId, params.offset, params.limit, params.sortBy, params.sortOrder, params.startDate, params.endDate, params.priorVersion, params.newerVersion);

            if (!Array.isArray(result.data)) {
                console.error('Invalid API response format:', result.data);
                return;
            }
            const formattedHistory = result.data.map(item => ({
                id: item.id || 0,
                date: item.updated_at || '',
                version1: item.params.compareContent[0].itemName + ' | ' + item.params.compareContent[0].title || '',
                version2: item.params.compareContent[1].itemName + ' | ' + item.params.compareContent[1].title || '',
                rating: item.rating || -1,
                selectedPromptId: item.prompt_id || 0,
                diffSummary: item.result || '',
                params: item.params
            }));
            commit(Mutation.SET_ITEM, { field: 'compareHistory', value: formattedHistory });
            commit(Mutation.SET_ITEM, { field: 'compareHistoryTotal', value: result.total });
            commit(Mutation.SET_ITEM, {field: 'compareHistoryLoading', value: false});
        } catch(error) {
            commit(Mutation.SET_ITEM, {field: 'compareHistoryLoading', value: false});
        }
    },
    deleteCompareHistory: async ({ commit, dispatch }, compareId) => {
        try {
            commit(Mutation.SET_ITEM, {field: 'compareHistoryLoading', value: true});
            await apiDeleteCompareHistory(compareId);
            commit(Mutation.SET_ITEM, {field: 'compareHistoryLoading', value: false});
        } catch(error) {
            console.log(error);
            commit(Mutation.SET_ITEM, {field: 'compareHistoryLoading', value: false});
        }
        dispatch('loadCompareHistory');
    },
    updateQuotaUsed: async ({ commit }) => {
        try {
            let quotaUsed = await apiGetCompareQueryCount();
            quotaUsed = isNaN(parseInt(quotaUsed, 10)) ? 0 : parseInt(quotaUsed, 10);
            commit(Mutation.SET_ITEM, {field: 'quotaUsed', value: quotaUsed});
        } catch(error) {
            console.log('api call to get quota used failed');
            console.log(error);
        }
    },
    setTOCForCompare: async ({ commit, getters, dispatch }, {versionType, itemId, itemName}) => {
        let tocType = versionType == 'prior' ? '1' : '2';
        commit(Mutation.SET_ITEM, {field: 'itemId' + tocType, value: itemId});
        commit(Mutation.SET_ITEM, {field: 'itemName' + tocType, value: itemName});
        commit(Mutation.SET_ITEM, {field: 'toc' + tocType + 'Selection', value: {}});
        commit(Mutation.SET_ITEM, {field: 'loading', value: true});
        try {
            const response = await dispatch('loadTOCsForCompare', {
                itemIds: [getters.getItem('itemId1'), getters.getItem('itemId2')]
            });
            commit(Mutation.SET_ITEM, {
                field: 'toc1',
                value: response.tocByItemId[parseInt(getters.getItem('itemId1'), 10)] || []
            });
            commit(Mutation.SET_ITEM, {
                field: 'toc2',
                value: response.tocByItemId[parseInt(getters.getItem('itemId2'), 10)] || []
            });
            if (response.state === 'processing') {
                commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: response.processingItems});
                commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: []});
                commit(Mutation.SET_ITEM, {field: 'loading', value: false});
                return;
            }
            if (response.state === 'failed') {
                commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: []});
                commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: response.failedItems});
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: ''});
                commit(Mutation.SET_ITEM, {field: 'loading', value: false});
                return;
            }
            if (response.state !== 'ready') {
                commit(Mutation.SET_ITEM, {field: 'tocModalNoticeKey', value: 'ai_compare_toc_unavailable'});
                commit(Mutation.SET_ITEM, {field: 'loading', value: false});
                return;
            }
            commit(Mutation.SET_ITEM, {field: 'tocProcessingItems', value: []});
            commit(Mutation.SET_ITEM, {field: 'tocFailedItems', value: []});
            const toc = response.tocByItemId[parseInt(itemId, 10)];
            if (Array.isArray(toc) && toc.length) {
                commit(Mutation.SET_ITEM, {field: 'toc' + tocType, value: toc});
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: ''});
            } else {
                commit(Mutation.SET_ITEM, {field: 'toc' + tocType, value: []});
                commit(Mutation.SET_ITEM, {field: 'compareVersionsError', value: 'ai_compare_toc_unavailable'});
            }
        } catch (error) {
            commit(Mutation.SET_ITEM, {field: 'toc' + tocType, value: []});
            commit(Mutation.SET_ITEM, {field: 'tocModalNoticeKey', value: 'ai_compare_toc_unavailable'});
            console.log('API call failed while loading PDF table of contents');
            console.log(error);
        }
        commit(Mutation.SET_ITEM, {field: 'loading', value: false});
    },
    resetCompareHistoryFilters: ({ commit }) => {
        commit(Mutation.SET_ITEM, {field: 'compareHistorySortBy', value: 'date'});
        commit(Mutation.SET_ITEM, {field: 'compareHistorySortDesc', value: false});
        commit(Mutation.SET_ITEM, {field: 'compareHistoryDateRange', value: ['', '']});
        commit(Mutation.SET_ITEM, {field: 'compareHistoryPriorVersion', value: ''});
        commit(Mutation.SET_ITEM, {field: 'compareHistoryNewerVersion', value: ''});
        commit(Mutation.SET_ITEM, {field: 'compareHistorySortOrder', value: 'desc'});
        commit(Mutation.SET_ITEM, {field: 'compareHistoryPage', value: 1});
        commit(Mutation.SET_ITEM, {field: 'compareHistoryItemsPerPage', value: 10});
    }
}
